import * as React from 'react'
import { DollarSign } from 'lucide-react'

import { cn, formatCurrencyInputValue } from '@/lib/utils'

import { InputGroup, type InputGroupProps } from './input-group'

export type CurrencyInputProps = Omit<
  InputGroupProps,
  'className' | 'inputClassName' | 'inputMode' | 'onBlur' | 'onChange' | 'startAdornment' | 'value'
> & {
  align?: 'left' | 'right'
  /** Applied to the input; use `wrapperClassName` to style the wrapper. */
  className?: string
  formatEmptyAsZero?: boolean
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onBlurValueChange?: (value: string, event: React.FocusEvent<HTMLInputElement>) => void
  onValueChange?: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void
  value?: string | number | null
  wrapperClassName?: string
}

const CurrencyInput = React.forwardRef<HTMLDivElement, CurrencyInputProps>(function CurrencyInput(
  {
    align = 'left',
    className = '',
    endAdornment = null,
    formatEmptyAsZero = true,
    onBlur,
    onBlurValueChange,
    onValueChange,
    size = 'default',
    value,
    wrapperClassName = '',
    ...props
  },
  ref
) {
  return (
    <InputGroup
      ref={ref}
      className={wrapperClassName}
      size={size}
      startAdornment={<DollarSign aria-hidden="true" />}
      endAdornment={endAdornment}
      inputClassName={cn(
        'w-full tabular-nums',
        align === 'right' ? 'text-right' : 'text-left',
        className
      )}
      inputMode="decimal"
      value={value ?? ''}
      onChange={(event) => onValueChange?.(event.target.value, event)}
      onBlur={(event) => {
        const nextValue = formatCurrencyInputValue(event.target.value, {
          formatEmptyAsZero,
        })

        if (nextValue !== event.target.value) {
          onBlurValueChange?.(nextValue, event)
        }

        onBlur?.(event)
      }}
      {...props}
    />
  )
})

export { CurrencyInput }
